import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@portal/auth';
import { aosColor, IconComponent, IconSize } from '@ui/aos-lib';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { selectUserSettingsForTemplate } from '../../../pages/help-and-settings/user-settings/store/user-settings.reducer';
import { AboutModalComponent } from '../about-modal/about-modal.component';

type OpenMenu = 'more' | 'user' | null;

@Component({
  selector: 'aos-header-actions',
  imports: [AsyncPipe, IconComponent, TranslateModule, AboutModalComponent],
  templateUrl: './header-actions.component.html',
  styleUrl: './header-actions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:click)': 'onDocumentClick($event)' }
})
export class HeaderActionsComponent {
  readonly #router = inject(Router);
  readonly #authService = inject(AuthService);
  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly aboutModal = viewChild.required(AboutModalComponent);

  protected readonly user$ = inject(Store).select(selectUserSettingsForTemplate);
  protected readonly openMenu = signal<OpenMenu>(null);

  protected readonly userIconColor = aosColor.primary500;
  protected readonly menuIconColor = aosColor.primary500;
  protected readonly iconSize = IconSize.SM;

  protected readonly aboutText = TranslateRegistry.get.ABOUT.MENU_ITEM;
  protected readonly helpText = TranslateRegistry.get.MAIN_MENU.TITLE.HELP;
  protected readonly aclaraConnectText = TranslateRegistry.get.MAIN_MENU.TITLE.ACLARA_CONNECT;
  protected readonly userSettingsText = TranslateRegistry.get.MAIN_MENU.TITLE.USER_SETTINGS;
  protected readonly logoutText = TranslateRegistry.get.MAIN_MENU.TITLE.LOG_OUT;

  private readonly helpLink = '/assets/help-page/';
  private readonly aclaraConnectLink = 'https://aclara.my.site.com/AclaraConnect/s/';

  protected toggle(menu: Exclude<OpenMenu, null>): void {
    this.openMenu.update(current => (current === menu ? null : menu));
  }

  protected onDocumentClick(event: Event): void {
    if (!this.#host.nativeElement.contains(event.target as Node)) {
      this.openMenu.set(null);
    }
  }

  protected openAbout(): void {
    this.openMenu.set(null);
    this.aboutModal().open();
  }

  protected openHelp(): void {
    this.openMenu.set(null);
    window.open(this.helpLink, '_blank');
  }

  protected openAclaraConnect(): void {
    this.openMenu.set(null);
    window.open(this.aclaraConnectLink, '_blank');
  }

  protected openUserSettings(): void {
    this.openMenu.set(null);
    this.#router.navigate(['/user-settings']);
  }

  protected logout(): void {
    this.openMenu.set(null);
    this.#authService.logout();
  }
}
